import { AmountCardUi } from "./AmountCard";
import { DeadlineCardUi } from "./DeadlineCard";
import { HistoryMessageCardUi } from "./HistoryMessageCard";
import { IncrementalCardUi } from "./IncrementalCard";
import { optionsType } from "./Index";
import { PercentageCardUi } from "./PercentageCard";
import { PointCardUi } from "./PointCard";
import { INormalizedLmcDataType, lmcOptionType } from "./types";
import { VolumeCardUi } from "./VolumeCard";
import {
  useGetEntity,
  useGetEntityPreferencesByPath,
} from "../../../../hooks/entity";
import useAuth from "../../../../hooks/useAuth";
import { Spinner } from "../../../atoms/Spinner";
import { CustomButton } from "../../../atoms/CustomButton";
import { DesktopViewRequiredMessage } from "../../../atoms/DesktopViewRequiredMessage";
import { useTranslation } from "react-i18next";
import { LuView } from "react-icons/lu";
import {
  MdQrCodeScanner,
  MdEdit,
  MdNotes,
  MdCheck,
  MdInfo,
  MdSignalCellularAlt,
  MdBolt,
} from "react-icons/md";

export default function MobilePreview({
  selectedLmcForCreation,
  currentPlaygroundData,
}: {
  selectedLmcForCreation: optionsType["value"];
  currentPlaygroundData: {
    type: lmcOptionType;
    data: INormalizedLmcDataType;
  };
}) {
  const { customer_identification, user } = useAuth();
  const { t } = useTranslation();

  const { data, isSuccess, isLoading, isError, refetch } =
    useGetEntityPreferencesByPath(
      customer_identification,
      user.entity_identification,
      "cdn_logo_project_path"
    );

  const {
    data: entityData,
    isSuccess: entityIsSuccess,
    isLoading: entityIsLoading,
    isError: entityIsError,
    refetch: entityRefetch,
  } = useGetEntity(customer_identification, user.entity_identification, {
    enabled: isSuccess,
  });

  const currentTime = new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });

  const lmcLabelMapperReverse = {
    pointCard: "POINT_CARD",
    historyMessageCard: "HIST_MESSAGE_CARD",
    percentageCard: "PERCENTAGE_CARD",
    incrementalCard: "INCREMENTAL_CARD",
    amountCard: "AMOUNT_CARD",
    deadlineCard: "DEADLINE_CARD",
    volumeCard: "VOLUME_CARD",
  };

  const renderProperUi = {
    pointCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <PointCardUi
          key={"pointCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    historyMessageCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <HistoryMessageCardUi
          key={"historyMessageCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    percentageCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <PercentageCardUi
          key={"percentageCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    incrementalCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <IncrementalCardUi
          key={"incrementalCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    amountCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <AmountCardUi
          key={"amountCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    deadlineCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <DeadlineCardUi
          key={"deadlineCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
    volumeCard:
      selectedLmcForCreation && currentPlaygroundData ? (
        <VolumeCardUi
          key={"volumeCard"}
          data={
            currentPlaygroundData.type ===
            lmcLabelMapperReverse[selectedLmcForCreation]
              ? currentPlaygroundData.data
              : null
          }
        />
      ) : null,
  };

  let content;

  if (isLoading || entityIsLoading)
    content = (
      <div
        className="flex flex-1 w-full h-full justify-center items-center"
        translate="no"
      >
        <Spinner size="sm" />
      </div>
    );

  if (isError || entityIsError)
    content = (
      <div
        className="flex flex-1 w-full h-full flex-col justify-center items-center gap-2"
        translate="no"
      >
        <CustomButton
          onClick={() => {
            refetch();
            entityRefetch();
          }}
          title="Reload"
        />
      </div>
    );

  if (isSuccess && entityIsSuccess) {
    content = (
      <div
        className="flex flex-col flex-1 bg-gray-950"
        style={{ paddingTop: "36px" }}
      >
        <div className="px-4 mx-2 py-4 bg-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              loading="lazy"
              className="object-cover w-8 h-8"
              src={data?.cdn_logo_project_path}
            />
            <span className="text-white text-lg font-semibold">
              {entityData?.entity_name}
            </span>
          </div>
          <button className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center">
            <MdQrCodeScanner className="w-5 h-5 text-gray-900" />
          </button>
        </div>

        <div className="px-4 py-4 bg-gray-800 rounded-md  mx-2 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="overflow-hidden w-12 h-12 rounded-full bg-white flex items-center justify-center">
              <img
                loading="lazy"
                className="w-full h-full object-cover"
                src="https://cdn.galloni-si.com/user/KX3T1UGILPS2J0USTQXN/25091021343329_6BN4IIHIHJ3TEFQPI9E6_image.png"
                alt="User avatar"
              />
            </div>
            <div className="flex-1">
              <span className="text-white text-sm">john.doe@example.com</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex-1 flex items-center justify-center gap-2 bg-gray-700 text-white py-2 px-4 rounded-full text-xs">
              <MdEdit className="w-4 h-4" />
              {t("common.edit")}
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 bg-gray-700 text-white py-2 px-4 rounded-full text-xs">
              <MdNotes className="w-4 h-4" />
              {t("dashboard.features.lmc.mobilePreview.notes")}
            </button>
          </div>
        </div>

        <div className="px-2 py-3">
          <div
            className="flex items-center justify-between px-4 py-1 rounded-full"
            style={{
              background:
                "linear-gradient(to right, rgb(34, 65, 115), rgb(22, 47, 81))",
            }}
          >
            <div className="w-4 h-4 bg-green-500 flex items-center justify-center rounded-sm">
              <MdCheck className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs font-regular text-white flex-1 text-center">
              {t("dashboard.features.lmc.mobilePreview.loggedInSuccessfully")}
            </span>
            <div className="w-6 h-6 rounded-full flex items-center justify-center">
              <MdInfo className="w-4 h-4 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="flex-1 px-2 pb-4 overflow-auto">
          {selectedLmcForCreation && renderProperUi[selectedLmcForCreation]}
        </div>
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="w-full">
        <div className="flex items-center gap-2 justify-center my-3">
          <LuView className="w-6 h-6 text-amber-500" />
          <h3 className="text-gray-900 text-lg font-bold font-figtree text-center">
            {t("dashboard.features.lmc.mobilePreview.title")}
          </h3>
        </div>

        <DesktopViewRequiredMessage descriptionKey="dashboard.features.lmc.mobilePreview.description" />

        <div className="hidden md:block relative w-full max-w-[360px] aspect-[9/16] justify-self-center mx-auto mb-24">
          <div
            className="relative w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-[3.2rem] p-3"
            style={{
              boxShadow: `
                0 4px 6px -1px rgba(0, 0, 0, 0.1),
                0 8px 15px -3px rgba(0, 0, 0, 0.15),
                0 12px 24px -6px rgba(0, 0, 0, 0.2),
                0 20px 40px -8px rgba(0, 0, 0, 0.25),
                0 32px 64px -12px rgba(0, 0, 0, 0.3),
                0 45px 80px -15px rgba(0, 0, 0, 0.35),
                inset 0 0 0 1px rgba(255, 255, 255, 0.12),
                inset 0 2px 6px 0 rgba(255, 255, 255, 0.08)
              `,
              transform: "perspective(1000px) rotateY(-2deg) rotateX(2deg)",
            }}
          >
            <div className="relative w-full h-full bg-black rounded-[2.6rem] overflow-hidden shadow-inner">
              <div
                className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20"
                style={{
                  width: "35%",
                  height: "28px",
                  background:
                    "linear-gradient(to bottom, #1a1a1a 0%, #000000 100%)",
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                }}
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 top-[8px] w-[10px] h-[10px] bg-gray-900 rounded-full border border-gray-700"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 top-[11px] ml-5 w-[20px] h-[4px] bg-gray-800 rounded-full"></div>
              </div>

              <div className="absolute top-0 left-0 right-0 z-10 px-6 pt-2 flex justify-between items-center text-white text-xs">
                <span className="font-semibold">{currentTime}</span>
                <div className="flex items-center gap-1.5">
                  <span>5G</span>
                  <MdSignalCellularAlt className="w-4 h-4" />

                  <div className="relative flex items-center">
                    <div
                      className="relative w-[22px] h-[11px] rounded-[2.5px] border-[1.5px] border-white flex items-center justify-end pr-[1.5px]"
                      style={{ opacity: 0.9 }}
                    >
                      <div
                        className="absolute left-[1.5px] top-[1.5px] bottom-[1.5px] bg-green-500 rounded-[1px]"
                        style={{
                          width: "75%",
                          boxShadow: "0 0 4px rgba(34, 197, 94, 0.6)",
                        }}
                      ></div>

                      <MdBolt className="w-[6px] h-[6px] text-white relative z-10" />
                    </div>

                    <div
                      className="w-[1.5px] h-[6px] bg-white rounded-r-[1px]"
                      style={{ opacity: 0.9 }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 bg-gray-950">{content}</div>
            </div>

            <div className="absolute -left-[3px] top-[100px] w-[3px] h-[30px] bg-gray-700 rounded-l-sm"></div>
            <div className="absolute -left-[3px] top-[140px] w-[3px] h-[50px] bg-gray-700 rounded-l-sm"></div>
            <div className="absolute -left-[3px] top-[200px] w-[3px] h-[50px] bg-gray-700 rounded-l-sm"></div>
            <div className="absolute -right-[3px] top-[150px] w-[3px] h-[70px] bg-gray-700 rounded-r-sm"></div>

            <div
              className="absolute left-1/2 transform -translate-x-1/2 w-[22px] h-[3px] bg-gradient-to-b from-gray-800 to-gray-950 rounded-sm"
              style={{
                bottom: "12px",
                boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.9)",
              }}
            ></div>
          </div>

          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
            <div className="relative flex flex-col items-center">
              <div
                className="w-[26px] h-[10px] bg-gradient-to-b from-white via-gray-100 to-gray-200 mt-[3px]"
                style={{
                  boxShadow:
                    "0 2px 4px rgba(0, 0, 0, 0.2), inset 0 0.5px 1px rgba(255, 255, 255, 0.6)",
                }}
              ></div>

              <div
                className="w-[24px] h-[18px] bg-gradient-to-b from-gray-200 via-white to-gray-100 relative"
                style={{
                  clipPath: "polygon(0 0, 100% 0, 85% 100%, 15% 100%)",
                  boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.06)",
                }}
              ></div>

              <div
                className="w-[16px] h-[70px] bg-gradient-to-r from-gray-100 via-white to-gray-100 relative overflow-hidden"
                style={{
                  boxShadow:
                    "inset -3px 0 4px rgba(0, 0, 0, 0.08), inset 3px 0 4px rgba(0, 0, 0, 0.08)",
                  borderRadius: "0 0 2px 2px",
                }}
              >
                <div className="absolute inset-0 flex flex-col justify-evenly">
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                  <div className="w-full h-[0.5px] bg-gray-300 opacity-30"></div>
                </div>
              </div>

              <div
                className="w-[15px] h-[25px] bg-gradient-to-b from-gray-100 via-gray-200 to-transparent opacity-70"
                style={{ borderRadius: "0 0 50% 50%" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
